import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ClickAwayListener, Grow, MenuItem, Paper, Popper } from '@mui/material';
import GoogleIcon from '../../assets/images/logo/GoogleIcon';
import { LoginMIDButton, LoginGoogleButton, LoginVNEIDButton } from '../../components/StyledButton';
import { useAuth as useAuthSSO } from "react-oidc-context";
import MobileIDSvg from '../../assets/images/icons/MobileIDSvg';
import VNEIDIcon from '../../assets/images/logo/VNeID.png';
import US from '../../assets/images/flag/US.png';
import VN from '../../assets/images/flag/VN.png';
import logo_light from '../../assets/images/logo/CheckIDBioSense_light.svg';
import ChevronDown10pxSvg from '../../assets/images/icons/ChevronDown10pxSvg';
import { NavbarLanguageMenuList } from '../../components/StyledComponents';
import { changeLanguage } from '../../redux/language/reducer';
import i18n from '../../n18i';

const Login = () => {
   const { t, i18n: i18nHook } = useTranslation();
   const authSSO = useAuthSSO();
   const dispatch = useDispatch();
   const anchorRefLanguage = useRef(null);
   const [openLanguage, setOpenLanguage] = useState(false);

   const currentLang = i18nHook.language?.startsWith('vi') ? 'vi' : 'en';
   const currentFlag = currentLang === 'vi' ? VN : US;

   const handleChangeLanguage = (language) => {
      i18n.changeLanguage(language);
      dispatch(changeLanguage(language));
      localStorage.setItem('i18nextLngId', language === 'vi' ? '1' : '2');
      setOpenLanguage(false);
   };

   return (
      <div className='relative w-full order-1 lg:order-2 lg:pb-60 flex justify-center items-center min-h-[80vh] md:min-h-[50vh] custom-min-h'>
         <div className="w-full max-w-5xl">
            {/* Logo + Language */}
            <div className="flex items-center justify-center gap-4 mb-6">
               <img
                  src={logo_light}
                  alt="CheckID BioSense"
                  width={300}
                  height={100}
               />
            </div>

            <div className="flex items-end justify-end gap-4 mb-6">

               <div className="relative">
                  <button
                     type="button"
                     ref={anchorRefLanguage}
                     onClick={() => setOpenLanguage((prev) => !prev)}
                     className="flex items-center gap-2 px-3 py-2 border border-[#E5E7EB] rounded-xl bg-white hover:bg-gray-50"
                     id="login-language-button"
                     aria-controls={openLanguage ? 'login-language-menu' : undefined}
                     aria-expanded={openLanguage ? 'true' : undefined}
                     aria-haspopup="true"
                  >
                     <img
                        src={currentFlag}
                        alt={currentLang === 'vi' ? 'VI' : 'EN'}
                        className="w-[22px] h-[22px] rounded-full object-cover"
                     />
                     <ChevronDown10pxSvg
                        className={`text-[#404040] transition-transform duration-300 ${
                           openLanguage ? 'rotate-180' : ''
                        }`}
                     />
                  </button>

                  <Popper
                     open={openLanguage}
                     anchorEl={anchorRefLanguage.current}
                     role={undefined}
                     placement="bottom-end"
                     transition
                     disablePortal
                     className="z-10"
                  >
                     {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                           <Paper
                              style={{
                                 borderRadius: '8px',
                                 marginTop: '8px',
                                 minWidth: 120,
                              }}
                           >
                              <ClickAwayListener onClickAway={() => setOpenLanguage(false)}>
                                 <NavbarLanguageMenuList
                                    autoFocusItem={openLanguage}
                                    sx={{
                                       overflow: 'hidden',
                                       borderRadius: '8px',
                                       '.MuiMenuItem-root': {
                                          borderRadius: 0,
                                          '&:hover': {
                                             borderRadius: 0,
                                          },
                                          '&:first-of-type:hover': {
                                             borderTopLeftRadius: '8px',
                                             borderTopRightRadius: '8px',
                                          },
                                          '&:last-of-type:hover': {
                                             borderBottomLeftRadius: '8px',
                                             borderBottomRightRadius: '8px',
                                          },
                                       },
                                    }}
                                 >
                                    <MenuItem onClick={() => handleChangeLanguage('en')}>
                                       <div className="flex items-center gap-2 whitespace-nowrap">
                                          <img src={US} alt="EN" className="w-4 h-4 rounded-full object-cover shrink-0" />
                                          <span>{t('common.languageEn')}</span>
                                       </div>
                                    </MenuItem>
                                    <MenuItem onClick={() => handleChangeLanguage('vi')}>
                                       <div className="flex items-center gap-2 whitespace-nowrap">
                                          <img src={VN} alt="VI" className="w-5 h-5 rounded-full object-cover shrink-0" />
                                          <span>{t('common.languageVi')}</span>
                                       </div>
                                    </MenuItem>
                                 </NavbarLanguageMenuList>
                              </ClickAwayListener>
                           </Paper>
                        </Grow>
                     )}
                  </Popper>
               </div>
            </div>

            <p className="font-extrabold text-[24px] text-[#1F2937] pb-5 leading-normal">{t('common.log-in-to-your-account')}</p>

            <div className="grid grid-cols-1 gap-y-6 w-full">
               <div>
                  <LoginMIDButton
                     className="w-full"
                     onClick={() =>
                        authSSO.signinRedirect({
                           scope: "openid profile identity email",
                           extraQueryParams: {
                              kc_locale: i18nHook.language,
                              ui_locales: i18nHook.language,
                              prompt: 'login'
                           },
                        })
                     }
                  >
                     <MobileIDSvg />
                     <p className="font-medium text-sm">{t('button.continue-with-mobileid')}</p>
                  </LoginMIDButton>
               </div>
               <div>
                  <LoginGoogleButton
                     className="w-full"
                     onClick={() =>
                        authSSO.signinRedirect({
                           scope: "openid profile identity email",
                           extraQueryParams: {
                              kc_idp_hint: "google",
                              prompt: "login",
                           },
                        })
                     }
                  >
                     <GoogleIcon />
                     <p className="font-medium text-sm text-[#4E4E4E]">{t('button.login-with-google')}</p>
                  </LoginGoogleButton>
               </div>

               <div>
                  <LoginVNEIDButton
                     className="w-full"
                     onClick={() =>
                        authSSO.signinRedirect({
                           scope: "openid profile identity email",
                           extraQueryParams: {
                              kc_idp_hint: "vneid",
                              prompt: "login",
                           },
                        })
                     }
                  >
                     <img src={VNEIDIcon} alt="VNEID" className="w-5 h-5" />
                     <p className="font-medium text-sm">{t('button.login-with-vneid')}</p>
                  </LoginVNEIDButton>
               </div>
            </div>
         </div>
      </div>
   );
};

export default Login;
